import { AqElement, cart, closeOverlay, config, debounce, emitCartUpdate, openOverlay, qs, toast } from '@aq/core';

const SECTION = 'cart-drawer';

class AqCartDrawer extends AqElement {
  init() {
    this.body = qs('[data-body]', this);

    this.listen(document, 'click', (event) => {
      const trigger = event.target.closest('[data-cart-trigger]');
      if (trigger && config.cartType === 'drawer') {
        event.preventDefault();
        this.open(trigger);
      }
    });
    this.listen(document, 'aq:cart:open', (event) => this.open(event.detail?.trigger, event.detail?.html));
    this.listen(document, 'aq:cart:update', (event) => {
      const html = event.detail?.sections?.[SECTION];
      if (html) this.#render(html);
    });

    this.listen(this, 'click', (event) => this.#onClick(event));
    this.listen(this, 'change', (event) => {
      if (event.target.matches('[data-cart-note]')) this.#saveNote(event.target.value);
    });
    this.listen(
      this,
      'input',
      debounce((event) => {
        if (event.target.matches('[data-cart-note]')) this.#saveNote(event.target.value);
      }, 600),
    );
  }

  async open(trigger = document.activeElement, html) {
    if (html) this.#render(html);
    else await this.refresh();
    openOverlay(this, { trigger });
  }

  async refresh() {
    try {
      const response = await fetch(`${config.routes.root}?sections=${SECTION}`);
      const data = await response.json();
      if (data[SECTION]) this.#render(data[SECTION]);
    } catch {
      /* the drawer still opens with its last known contents */
    }
  }

  #render(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = qs('[data-body]', doc);
    if (!next) return;
    const focused = document.activeElement;
    const key = focused?.closest('[data-key]')?.dataset.key;
    const action = focused?.matches('[data-qty-change]') ? `[data-qty-change="${focused.dataset.qtyChange}"]` : '[data-remove]';

    this.body.innerHTML = next.innerHTML;

    if (key && this.contains(focused)) {
      (qs(`[data-key="${key}"] ${action}`, this) || qs('[data-close]', this))?.focus();
    }
  }

  async #onClick(event) {
    if (event.target.closest('[data-close]')) {
      closeOverlay(this);
      return;
    }

    const line = event.target.closest('[data-key]');
    const qtyButton = event.target.closest('[data-qty-change]');
    const removeButton = event.target.closest('[data-remove]');

    if (line && (qtyButton || removeButton)) {
      const current = Number(qs('.aq-qty span', line).textContent);
      const quantity = removeButton ? 0 : Math.max(0, current + Number(qtyButton.dataset.qtyChange));
      await this.#run(() => cart.change(line.dataset.key, quantity, SECTION));
      return;
    }

    if (event.target.closest('[data-discount-apply]')) {
      const input = qs('[data-discount-input]', this);
      if (input?.value.trim()) await this.#run(() => cart.update({ discount: input.value.trim(), sections: SECTION }));
    }
  }

  async #saveNote(note) {
    try {
      await cart.update({ note });
    } catch (error) {
      toast(error.message);
    }
  }

  async #run(action) {
    this.setAttribute('aria-busy', 'true');
    try {
      const data = await action();
      emitCartUpdate(data);
    } catch (error) {
      toast(error.message);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('aq-cart-drawer')) customElements.define('aq-cart-drawer', AqCartDrawer);
